import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoadingPage from '../ui/LoadingPage';

interface PublicRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * PublicRoute component for authentication pages
 * - Redirects authenticated users away from auth pages
 * - Shows loading spinner while session is being restored
 * - Handles return URL after login
 */
const PublicRoute: React.FC<PublicRouteProps> = ({ 
  children, 
  redirectTo = '/' 
}) => {
  const { isAuthenticated, isSessionRestored, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while session is being restored
  if (!isSessionRestored || isLoading) {
    return (
      <LoadingPage 
        title="Loading..." 
        subtitle="Checking authentication" 
      />
    );
  }

  // If authenticated, redirect to home or specified redirect
  if (isAuthenticated) {
    // Check for return URL in query params
    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get('returnUrl');
    
    if (returnUrl) {
      return <Navigate to={returnUrl} replace />;
    }
    
    return <Navigate to={redirectTo} replace />;
  }

  // User is not authenticated, render auth page
  return <>{children}</>;
};

export default PublicRoute;
